import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import api from '../../services/api';
import { Smartphone, Building2, Zap, Wallet, CreditCard, ChevronDown, Check, Trash2 } from 'lucide-react';

const PAYMENT_METHODS = [
    {
        id: 'UPI',
        name: 'UPI',
        Icon: Smartphone,
        color: 'text-blue-400',
        fields: [
            { key: 'upiId', label: 'UPI ID', placeholder: 'yourname@upi', type: 'text' }
        ]
    },
    {
        id: 'Bank Transfer',
        name: 'Bank Transfer',
        Icon: Building2,
        color: 'text-emerald-400',
        fields: [
            { key: 'accountName', label: 'Account Holder Name', placeholder: 'John Doe', type: 'text' },
            { key: 'accountNumber', label: 'Account Number', placeholder: '1234567890', type: 'text' },
            { key: 'ifscCode', label: 'IFSC Code', placeholder: 'SBIN0001234', type: 'text' },
            { key: 'bankName', label: 'Bank Name', placeholder: 'State Bank of India', type: 'text' }
        ]
    },
    {
        id: 'IMPS',
        name: 'IMPS',
        Icon: Zap,
        color: 'text-yellow-400',
        fields: [
            { key: 'accountName', label: 'Account Holder Name', placeholder: 'John Doe', type: 'text' },
            { key: 'accountNumber', label: 'Account Number', placeholder: '1234567890', type: 'text' },
            { key: 'ifscCode', label: 'IFSC Code', placeholder: 'SBIN0001234', type: 'text' },
            { key: 'bankName', label: 'Bank Name', placeholder: 'State Bank of India', type: 'text' }
        ]
    },
    {
        id: 'Google Pay',
        name: 'Google Pay',
        Icon: Wallet,
        color: 'text-red-400',
        fields: [
            { key: 'mobileNumber', label: 'Mobile Number', placeholder: '9876543210', type: 'tel' }
        ]
    },
    {
        id: 'PhonePe',
        name: 'PhonePe',
        Icon: CreditCard,
        color: 'text-purple-400',
        fields: [
            { key: 'mobileNumber', label: 'Mobile Number', placeholder: '9876543210', type: 'tel' }
        ]
    }
];

const PaymentMethodsTab = () => {
    const [savedMethods, setSavedMethods] = useState([]);
    const [formData, setFormData] = useState({});
    const [loading, setLoading] = useState({});
    const [expandedMethod, setExpandedMethod] = useState(null);

    useEffect(() => {
        fetchPaymentMethods();
    }, []);

    const fetchPaymentMethods = async () => {
        try {
            const res = await api.get('/p2p/payment-methods');
            const methods = res.data.paymentMethods || [];
            setSavedMethods(methods);

            // Pre-fill form data with saved methods
            const initialData = {};
            methods.forEach(m => {
                initialData[m.method] = m.details || {};
            });
            setFormData(initialData);
        } catch (error) {
            console.error('Failed to fetch payment methods:', error);
        }
    };

    const isMethodConfigured = (methodId) => {
        return savedMethods.some(m => m.method === methodId && m.active);
    };

    const handleInputChange = (methodId, fieldKey, value) => {
        setFormData(prev => ({
            ...prev,
            [methodId]: {
                ...(prev[methodId] || {}),
                [fieldKey]: value
            }
        }));
    };

    const handleSave = async (methodId) => {
        const details = formData[methodId];
        const method = PAYMENT_METHODS.find(m => m.id === methodId);

        // Validate all fields are filled
        const allFilled = method.fields.every(f => details?.[f.key]?.trim());
        if (!allFilled) {
            toast.error('Please fill all fields');
            return;
        }

        setLoading(prev => ({ ...prev, [methodId]: true }));
        try {
            await api.post('/p2p/payment-methods', {
                method: methodId,
                details,
                active: true
            });
            toast.success(`${methodId} details saved!`);
            fetchPaymentMethods();
            setExpandedMethod(null);
        } catch (error) {
            toast.error(error.response?.data?.error || 'Failed to save');
        } finally {
            setLoading(prev => ({ ...prev, [methodId]: false }));
        }
    };

    const handleDelete = async (methodId) => {
        if (!window.confirm(`Delete ${methodId} details?`)) return;

        setLoading(prev => ({ ...prev, [methodId]: true }));
        try {
            await api.delete(`/p2p/payment-methods/${encodeURIComponent(methodId)}`);
            toast.success(`${methodId} details deleted`);
            setFormData(prev => ({ ...prev, [methodId]: {} }));
            fetchPaymentMethods();
        } catch (error) {
            toast.error('Failed to delete');
        } finally {
            setLoading(prev => ({ ...prev, [methodId]: false }));
        }
    };

    return (
        <div className="max-w-3xl mx-auto py-6">
            <div className="mb-6">
                <h2 className="text-2xl font-bold text-white mb-2">Payment Methods</h2>
                <p className="text-gray-400">Add your payment details. Only configured methods can be used when creating P2P ads.</p>
            </div>

            <div className="space-y-4">
                {PAYMENT_METHODS.map(method => {
                    const isConfigured = isMethodConfigured(method.id);
                    const isExpanded = expandedMethod === method.id;
                    const methodDetails = formData[method.id] || {};

                    return (
                        <div
                            key={method.id}
                            className={`bg-[#1e2026] border rounded-xl overflow-hidden transition-all ${isConfigured ? 'border-green-600' : 'border-gray-700'
                                }`}
                        >
                            {/* Header */}
                            <div
                                className="flex items-center justify-between p-4 cursor-pointer hover:bg-[#252830]"
                                onClick={() => setExpandedMethod(isExpanded ? null : method.id)}
                            >
                                <div className="flex items-center gap-3">
                                    <div className={`p-2 rounded-lg bg-gray-800 ${method.color}`}>
                                        <method.Icon size={20} />
                                    </div>
                                    <span className="text-white font-medium">{method.name}</span>
                                    {isConfigured && (
                                        <span className="flex items-center gap-1 bg-green-500/20 text-green-400 text-xs px-2 py-1 rounded-full">
                                            <Check size={12} />
                                            Configured
                                        </span>
                                    )}
                                </div>
                                <ChevronDown
                                    className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                                />
                            </div>

                            {/* Expanded Form */}
                            {isExpanded && (
                                <div className="border-t border-gray-700 p-4 bg-[#161920]">
                                    <div className="grid gap-4">
                                        {method.fields.map(field => (
                                            <div key={field.key}>
                                                <label className="block text-xs font-bold text-gray-400 uppercase mb-2">
                                                    {field.label}
                                                </label>
                                                <input
                                                    type={field.type}
                                                    value={methodDetails[field.key] || ''}
                                                    onChange={(e) => handleInputChange(method.id, field.key, e.target.value)}
                                                    className="w-full bg-[#0b0e11] border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-blue-500"
                                                    placeholder={field.placeholder}
                                                />
                                            </div>
                                        ))}
                                    </div>

                                    <div className="flex gap-3 mt-4">
                                        <button
                                            onClick={() => handleSave(method.id)}
                                            disabled={loading[method.id]}
                                            className="flex-1 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-500 disabled:opacity-50"
                                        >
                                            {loading[method.id] ? 'Saving...' : 'Save'}
                                        </button>
                                        {isConfigured && (
                                            <button
                                                onClick={() => handleDelete(method.id)}
                                                disabled={loading[method.id]}
                                                className="flex items-center gap-2 px-6 py-3 bg-red-600/20 text-red-400 rounded-lg font-medium hover:bg-red-600/30"
                                            >
                                                <Trash2 size={16} />
                                                Delete
                                            </button>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default PaymentMethodsTab;
